import { cn, Skeleton } from '@proerp/ui';

/**
 * Eine Kennzahl-Kachel des Portals (ADR 0085).
 *
 * Drei Dinge, die sie bewusst tut:
 *
 * 1. **Feste Höhe, auch im Ladezustand.** Springt die Kachelreihe beim
 *    Eintreffen der Daten, verschiebt sich alles darunter — der Klick geht
 *    daneben. Deshalb rendert der Ladezustand dieselbe Geometrie.
 * 2. **Der Wert steht vor der Erklärung.** Ein Lieferant liest die Zahl, nicht
 *    das Label.
 * 3. **Kein Farbcode als einzige Aussage** (WCAG): Der Ton begleitet, die
 *    Bedeutung steht im Text.
 */
export type StatTone = 'default' | 'accent' | 'warning';

const TONE_RING: Record<StatTone, string> = {
  default: 'border-border',
  accent: 'border-[color:var(--chart-1)]/35',
  warning: 'border-warning-500/40',
};

const TONE_ICON: Record<StatTone, string> = {
  default: 'bg-muted text-muted-foreground',
  accent: 'bg-[color:var(--chart-1)]/10 text-[color:var(--chart-1)]',
  warning: 'bg-warning-50 text-warning-500',
};

export function StatTile({
  label,
  value,
  hint,
  icon,
  tone = 'default',
  isLoading = false,
}: {
  label: string;
  value: string;
  /** Zusatz unter dem Wert — Einheit, Datum, Einordnung. */
  hint?: string;
  icon?: React.ReactNode;
  tone?: StatTone;
  isLoading?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex min-h-[7.5rem] flex-col justify-between rounded-xl border bg-card p-4 shadow-sm',
        TONE_RING[tone],
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        {icon && (
          <span
            className={cn('flex size-8 shrink-0 items-center justify-center rounded-lg', TONE_ICON[tone])}
            aria-hidden
          >
            {icon}
          </span>
        )}
      </div>
      {isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-7 w-24" />
          <Skeleton className="h-3 w-32" />
        </div>
      ) : (
        <div>
          <p className="text-2xl font-semibold tabular-nums tracking-tight text-foreground">{value}</p>
          {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
        </div>
      )}
    </div>
  );
}
